import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { CompRow } from 'common/constants/dailyNavComps';
import { useLookupCompAlexandriaId } from './useDailyNavConfig';

// The comps watchlist draft shared by the company tab and the setup wizard. Added comps are
// looked up read-only; callers hold saves while isResolvingLinks so saves carry resolved ids.
export const useCompsWatchlist = (firmId: number | null, companyId: number | null) => {
  const { enqueueSnackbar } = useSnackbar();
  const { lookupCompAlexandriaId } = useLookupCompAlexandriaId();
  const [comps, setComps] = useState<CompRow[]>([]);
  const [pendingLookups, setPendingLookups] = useState(0);
  // A lookup that lands after unmount must not warn on whatever screen is showing now.
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const resolveCompLinks = useCallback(
    async (added: CompRow[]) => {
      if (!firmId || !companyId || !added.length) {
        return;
      }
      setPendingLookups(count => count + 1);
      try {
        const results = await Promise.all(
          added.map(async row => ({
            row,
            lookup: await lookupCompAlexandriaId(firmId, companyId, row.ticker_symbol),
          }))
        );
        if (!isMountedRef.current) {
          return;
        }

        // Matched by cap_iq_id, not position: rows may have moved while in flight.
        const linked = new Map(
          results
            .filter(({ lookup }) => lookup?.alexandria_id)
            .map(({ row, lookup }) => [row.cap_iq_id, lookup?.alexandria_id ?? null])
        );
        if (linked.size) {
          setComps(prevState =>
            prevState.map(comp =>
              linked.has(comp.cap_iq_id) ? { ...comp, alexandria_id: linked.get(comp.cap_iq_id) ?? null } : comp
            )
          );
        }

        const pending = results
          .filter(({ lookup }) => lookup && !lookup.alexandria_id)
          .map(({ row }) => row.company_name);
        if (pending.length) {
          enqueueSnackbar(
            `${pending.join(', ')} ${pending.length > 1 ? 'are' : 'is'} not tracked yet. ` +
              'Saving will request it, and price history usually takes a few hours to arrive.',
            { variant: 'warning' }
          );
        }
      } finally {
        if (isMountedRef.current) {
          setPendingLookups(count => count - 1);
        }
      }
    },
    [firmId, companyId, lookupCompAlexandriaId, enqueueSnackbar]
  );

  // The saved list, with no lookups: it was already resolved when saved.
  const resetComps = useCallback((rows: CompRow[]) => setComps(rows), []);

  // Replaces the list wholesale, e.g. the company tab's one-time copy from a GPC approach.
  const replaceComps = useCallback(
    (rows: CompRow[]) => {
      setComps(rows);
      resolveCompLinks(rows);
    },
    [resolveCompLinks]
  );

  // Appends, skipping any comp already present (by cap_iq_id) or repeated in `rows`.
  const addComps = useCallback(
    (rows: CompRow[]) => {
      // Outside the updater: updaters must stay pure, and StrictMode double-invokes
      // them, which would look up and warn twice.
      const seenIds = new Set(comps.map(comp => comp.cap_iq_id));
      const additions = rows.filter(row => {
        if (!row.cap_iq_id || seenIds.has(row.cap_iq_id)) {
          return false;
        }
        seenIds.add(row.cap_iq_id);
        return true;
      });
      if (!additions.length) {
        return;
      }
      setComps(prevState => [...prevState, ...additions]);
      resolveCompLinks(additions);
    },
    [comps, resolveCompLinks]
  );

  const removeComp = useCallback((capIqId: string) => {
    setComps(prevState => prevState.filter(comp => comp.cap_iq_id !== capIqId));
  }, []);

  return { comps, resetComps, replaceComps, addComps, removeComp, isResolvingLinks: pendingLookups > 0 };
};
